/**
 * User Payment History Column Definitions
 *
 * Column definitions for the UserPaymentHistory DataTable.
 */

import { type ColumnDef } from "@tanstack/react-table";
import type { HistoricalTransaction } from "@/services/paymentService";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, XCircle, Clock, AlertCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { formatDateTime } from "../utils/userDetailUtils";

const getPaymentData = (payment: HistoricalTransaction): any => {
  return (payment as any)._doc || payment;
};

const getCallsign = (payment: HistoricalTransaction): string => {
  const paymentData = getPaymentData(payment);
  if (paymentData.paymentId) {
    const parts = paymentData.paymentId.split("-");
    return parts[0] || "-";
  }
  const user = paymentData.user;
  if (user && typeof user === "object" && "callsign" in user) {
    return user.callsign || "-";
  }
  return "-";
};

const formatAmount = (payment: HistoricalTransaction): string => {
  const paymentData = getPaymentData(payment);
  const amount =
    payment.midtransStatus?.gross_amount ??
    paymentData.payementData?.transaction_details?.gross_amount;
  if (amount) {
    return new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
    }).format(parseInt(amount));
  }
  return "-";
};

const getPaymentChannel = (payment: HistoricalTransaction): string => {
  const paymentData = getPaymentData(payment);
  return (
    payment.midtransStatus?.payment_type ??
    paymentData.payementData?.payment_type ??
    paymentData.paymentGatewayData?.payment_type ??
    paymentData.payementData?.channel ??
    "-"
  );
};

const getRemark = (payment: HistoricalTransaction): string => {
  const paymentData = getPaymentData(payment);
  return (
    paymentData.payementData?.transaction_details?.note ??
    paymentData.paymentGatewayData?.note ??
    payment.midtransStatus?.status_message ??
    "-"
  );
};

function getStatusInfo(payment: HistoricalTransaction): { label: string; className: string; icon: React.ReactNode } {
  const paymentData = getPaymentData(payment);
  const status = payment.midtransStatus?.transaction_status ?? paymentData.status;

  switch (status?.toLowerCase()) {
    case "settlement":
    case "capture":
      return {
        label: "Berhasil",
        className: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 border-green-200 dark:border-green-800",
        icon: <CheckCircle2 className="h-3 w-3" />,
      };
    case "pending":
      return {
        label: "Menunggu",
        className: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200 border-yellow-200 dark:border-yellow-800",
        icon: <Clock className="h-3 w-3" />,
      };
    case "deny":
    case "cancel":
    case "expire":
      return {
        label: "Gagal",
        className: "",
        icon: <XCircle className="h-3 w-3" />,
      };
    default:
      return {
        label: status || "Unknown",
        className: "",
        icon: <AlertCircle className="h-3 w-3" />,
      };
  }
}

export const userPaymentColumns: ColumnDef<HistoricalTransaction>[] = [
  {
    id: "tanggal",
    accessorFn: (row) => getPaymentData(row).createdAt,
    header: "Tanggal",
    cell: ({ row }) => (
      <span className="text-sm text-muted-foreground">
        {formatDateTime(getPaymentData(row.original).createdAt)}
      </span>
    ),
    enableSorting: false,
  },
  {
    id: "callsign",
    accessorFn: (row) => getCallsign(row),
    header: "Callsign",
    cell: ({ row }) => (
      <span className="font-mono text-sm">{getCallsign(row.original)}</span>
    ),
    enableSorting: false,
  },
  {
    id: "jumlahBayar",
    accessorFn: (row) => {
      const paymentData = getPaymentData(row);
      const amount = row.midtransStatus?.gross_amount ?? paymentData.payementData?.transaction_details?.gross_amount;
      return amount ? parseInt(amount, 10) : 0;
    },
    header: "Jumlah Bayar",
    cell: ({ row }) => (
      <span className="font-medium">{formatAmount(row.original)}</span>
    ),
    enableSorting: false,
  },
  {
    id: "channel",
    accessorFn: (row) => getPaymentChannel(row),
    header: "Channel Pembayaran",
    cell: ({ row }) => (
      <span className="text-sm">{getPaymentChannel(row.original)}</span>
    ),
    enableSorting: false,
  },
  {
    id: "status",
    accessorFn: (row) => {
      const paymentData = getPaymentData(row);
      return row.midtransStatus?.transaction_status ?? paymentData.status ?? "";
    },
    header: "Status",
    cell: ({ row }) => {
      const info = getStatusInfo(row.original);
      return (
        <Badge
          variant="secondary"
          className={cn("flex items-center gap-1", info.className)}
        >
          {info.icon}
          {info.label}
        </Badge>
      );
    },
    enableSorting: false,
  },
  {
    id: "remark",
    accessorFn: (row) => getRemark(row),
    header: "Remark",
    cell: ({ row }) => (
      <span className="text-sm">{getRemark(row.original)}</span>
    ),
    enableSorting: false,
  },
];
